import * as React from 'react';

import {
  enumSalesItemLineType,
  enumSalesLoadLineItemPhase,
  enumUnit,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { cn, Widget } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideTag } from 'lucide-react';

import { useWeightConversion } from '../../../hooks/useWeightConversion';
import { InventoryItemButton } from '../../_shared/InventoryItemButton';

import {
  $inventoryItemsById,
  $lineItemsByPhaseKey,
  $salesOrderLineItems,
} from '~/modules/trade/_state/loadForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
  WeightUnitType,
} from '~/modules/trade/types/load';
import { calculateRemainingWeight } from '~/modules/trade/utils/weight';
import { getLocale } from '~/shared/utils/locale';

type RequestedMaterialsListProps = {
  facilityDefaultUnit: Unit;
  getLineItemWeightUnit: (item: LoadFormLineItem) => WeightUnitType;
  onSelectMaterial: (itemId: string) => void;
};

const REQUESTED_MATERIAL_BUTTON_CLASS_NAME = cn(
  '@RequestedMaterialButton',
  'flex !h-11 !min-w-35 items-center justify-center gap-2 self-stretch !rounded-md !border-none !bg-neutral/5 p-1 text-sm font-normal text-neutral !outline-none',
  'hover:cursor-pointer hover:bg-neutral/10 hover:text-neutral/70 hover:outline-neutral/20',
);

type RequestedMaterialInfoProps = {
  requestedLineItem: LoadFormLineItem;
  stagedTotalWeight?: Weight;
  facilityDefaultUnit: Unit;
  getLineItemWeightUnit: (item: LoadFormLineItem) => WeightUnitType;
};

const RequestedMaterialInfo = ({
  getLineItemWeightUnit,
  facilityDefaultUnit,
  requestedLineItem,
  stagedTotalWeight,
}: RequestedMaterialInfoProps) => {
  const isTagged = requestedLineItem.type === enumSalesItemLineType.Tagged;

  const requestedWeight = requestedLineItem.weights?.[enumLoadWeightType.Net];

  const weightUnit = getLineItemWeightUnit(requestedLineItem);

  const { convertToDisplayUnit, massUnitAbbreviation } = useWeightConversion({
    weightUnit,
    facilityDefaultUnit,
    pricingUnit: requestedLineItem.pricingUnit as Unit,
  });

  const remainingRequestedWeight = React.useMemo(
    () => calculateRemainingWeight(requestedWeight, stagedTotalWeight),
    [requestedWeight, stagedTotalWeight],
  );

  const displayWeight = convertToDisplayUnit(
    remainingRequestedWeight,
  ).toLocaleString(getLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });

  return (
    <div className="@RequestedMaterialInfo flex items-center gap-2 text-xs font-normal text-neutral/60">
      {isTagged && (
        <span className="flex items-center gap-1">
          <LucideTag size={12} />{' '}
          {requestedLineItem.aggregatedTags?.length ?? 0}{' '}
        </span>
      )}
      <span>
        R {displayWeight} {massUnitAbbreviation}
      </span>
    </div>
  );
};

type StagedItemEntry = { item: LoadFormLineItem; totalWeight: Weight };

export const RequestedMaterialsList = ({
  facilityDefaultUnit,
  getLineItemWeightUnit,
  onSelectMaterial,
}: RequestedMaterialsListProps) => {
  const t = useTranslations();
  const inventoryItemsById = useAtomValue($inventoryItemsById);
  const { data: salesOrderLineItemsData } = useAtomValue($salesOrderLineItems);

  const salesOrderItemIds = React.useMemo(() => {
    if (!salesOrderLineItemsData?.salesOrders?.entries?.[0]) {
      return new Set<string>();
    }
    const entry = salesOrderLineItemsData.salesOrders.entries[0];
    return new Set(
      entry.lineItems
        .map((lineItem) => lineItem.item.id)
        .filter((id): id is string => !!id),
    );
  }, [salesOrderLineItemsData]);

  const lineItemsByPhaseKey = useAtomValue($lineItemsByPhaseKey);

  const requestedItems = React.useMemo(
    () => lineItemsByPhaseKey[enumSalesLoadLineItemPhase.Requested],
    [lineItemsByPhaseKey],
  );

  const stagedItems = React.useMemo(
    () => lineItemsByPhaseKey[enumSalesLoadLineItemPhase.Staged],
    [lineItemsByPhaseKey],
  );

  /*
   * 🟢🟢🟢 Create staged items map with aggregated weights for O(1) lookups
   */
  const stagedItemsMap = React.useMemo(() => {
    const itemsWithIds = stagedItems.filter(
      (item): item is LoadFormLineItem & { itemId: string } => !!item.itemId,
    );

    return itemsWithIds.reduce((acc, item) => {
      const weightValue = Number(
        item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
      );
      const existing = acc.get(item.itemId);

      const totalWeightValue = existing
        ? existing.totalWeight.value + weightValue
        : weightValue;

      const entry: StagedItemEntry = {
        item: existing?.item ?? item,
        totalWeight: {
          value: totalWeightValue,
          unit: enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
      };

      return acc.set(item.itemId, entry);
    }, new Map<string, StagedItemEntry>());
  }, [stagedItems]);

  /*
   * 🟢🟢🟢 Filter and sort requested lineItems for materials that are remaining to be staged
   */
  const filteredAndSortedItems = React.useMemo(
    () =>
      requestedItems
        .filter((item) => {
          if (!item.itemId) return true;

          const stagedData = stagedItemsMap.get(item.itemId);
          if (!stagedData || stagedData.totalWeight.value === 0) return true;

          const requestedNetWeight = Number(
            item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
          );

          return stagedData.totalWeight.value < requestedNetWeight;
        })
        .sort((a, b) => {
          const aInOrder = a.itemId && salesOrderItemIds.has(a.itemId);
          const bInOrder = b.itemId && salesOrderItemIds.has(b.itemId);
          if (aInOrder && !bInOrder) return -1;
          if (!aInOrder && bInOrder) return 1;
          return 0;
        }),
    [requestedItems, stagedItemsMap, salesOrderItemIds],
  );

  return (
    <Widget
      className={cn('@RequestedMaterialsList', 'overflow-y-auto px-1 pb-2')}
    >
      <div className="flex flex-col gap-2">
        <div className="sticky top-0 bg-white px-3 py-2 text-sm font-semibold text-neutral/60">
          {t.loadStaged.requestedMaterials.toUpperCase()} (
          {filteredAndSortedItems.length})
        </div>
        {filteredAndSortedItems.map((item) => {
          const material = inventoryItemsById[item.itemId ?? ''];
          if (!material) return null;

          const matchingStagedItem = stagedItemsMap.get(
            item.itemId ?? '',
          )?.totalWeight;

          return (
            <InventoryItemButton
              key={item.id}
              inventoryItem={material}
              isSelected={false}
              align="left"
              className={REQUESTED_MATERIAL_BUTTON_CLASS_NAME}
              itemSubtitle={
                <RequestedMaterialInfo
                  getLineItemWeightUnit={getLineItemWeightUnit}
                  facilityDefaultUnit={facilityDefaultUnit}
                  requestedLineItem={item}
                  stagedTotalWeight={matchingStagedItem}
                />
              }
              onPress={() => onSelectMaterial(item.id)}
            />
          );
        })}
      </div>
    </Widget>
  );
};
